import React from "react";
import TagWithOverflow from "../../../tagWithOverflow/tagWithOverflow";
import "./TagWithOverflowCell.scss";

/**
 * Value shape for {@link TagWithOverflowCell} (Figma node 27:9708).
 *
 * Renders up to `maxVisibleTags` chips and an optional `+N` count for the
 * remaining items. Delegates to the standalone `TagWithOverflow` control.
 */
export interface TagWithOverflowCellValue {
  /**
   * Tag labels. Up to `maxVisibleTags` entries are rendered as chips;
   * remaining entries drive the `+N` overflow count when `overflowCount`
   * is omitted.
   */
  tags?: string[];
  /**
   * How many `tags` to render as chips before collapsing the rest into the
   * `+N` overflow. Defaults to `1` (chip + overflow).
   */
  maxVisibleTags?: number;
  /**
   * Primary tag text when not using `tags`. Used as the chip label.
   */
  text?: string;
  /**
   * Explicit overflow count shown as `+N`. When omitted, derived from the
   * number of hidden `tags` (or `0` if only `text` is provided).
   */
  overflowCount?: number;
  /**
   * Optional labels listed in the `+N` hover tooltip when `tags` does not
   * include the overflow items (e.g. when using `text` + `overflowCount`).
   */
  overflowLabels?: string[];
  /** Chip background color. Defaults to Primary Gray 16 (`#F5F5F5`). */
  backgroundColor?: string;
  /** Chip text color. Defaults to Primary Gray 1 (`#0F0F0F`). */
  foregroundColor?: string;
}

interface TagWithOverflowCellProps {
  value: TagWithOverflowCellValue;
}

/**
 * Table cell matching the Clinical Trials Figma tag + overflow pattern.
 * Thin wrapper over the standalone `TagWithOverflow` control so table and
 * non-table call sites share one implementation.
 *
 * @param props - Cell value with tags/text and optional colors
 * @returns Tag chips with optional overflow count, or an em dash when empty
 */
export const TagWithOverflowCell: React.FC<TagWithOverflowCellProps> = ({
  value,
}) => {
  if (!value) {
    return <span>—</span>;
  }

  const hasContent =
    (value.tags?.filter(Boolean).length ?? 0) > 0 || Boolean(value.text);
  if (!hasContent) {
    return <span>—</span>;
  }

  return (
    <TagWithOverflow
      className="table-cell-tag-with-overflow"
      tags={value.tags}
      maxVisibleTags={value.maxVisibleTags}
      text={value.text}
      overflowCount={value.overflowCount}
      overflowLabels={value.overflowLabels}
      backgroundColor={value.backgroundColor}
      foregroundColor={value.foregroundColor}
    />
  );
};

export default TagWithOverflowCell;
